import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { setToken, setSessionId, clearAuth, hasAuth, api, type PhoneLoginUser } from '../lib/api'
import { resetUserState, signOut } from '../lib/auth'
import { useMessages } from '@/i18n/messages'
import { loginMessages } from '@/i18n/text/login'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

const inputClass =
  'mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none'

function LoginPage() {
  const m = useMessages(loginMessages)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: methods } = useQuery({ queryKey: ['auth-methods'], queryFn: api.authMethods })
  const [tab, setTab] = useState<'password' | 'phone'>('password')
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [pendingLogin, setPendingLogin] = useState<{ loginId: string; securityNumber: string } | null>(null)
  const [accountChoices, setAccountChoices] = useState<PhoneLoginUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => () => stopPolling(), [])

  function stopPolling() {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = null
  }

  async function finishLogin(res: { token: string; sessionId: string | null }) {
    setToken(res.token)
    if (res.sessionId) setSessionId(res.sessionId)
    await resetUserState(queryClient)
    navigate({ to: '/' })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await finishLogin(await api.login(user, password))
    } catch (err) {
      clearAuth()
      setError((err as Error).message || m.loginFailed)
    } finally {
      setLoading(false)
    }
  }

  async function handlePhoneSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const start = await api.loginPhone(phone)
      setPendingLogin({ loginId: start.loginId, securityNumber: start.securityNumber })
      let polling = false
      pollRef.current = setInterval(async () => {
        if (polling) return
        polling = true
        try {
          const { status, users, message } = await api.loginPhoneStatus(start.loginId)
          if (status === 'pending') return
          stopPolling()
          if (status === 'approved') {
            if (users && users.length > 1) {
              setAccountChoices(users)
            } else {
              await finishLogin(await api.loginPhoneConfirm(start.loginId))
            }
          } else {
            setPendingLogin(null)
            setError(
              status === 'rejected'
                ? message || m.rejected
                : status === 'failed'
                  ? m.failed
                  : m.expired
            )
          }
        } catch (err) {
          stopPolling()
          setPendingLogin(null)
          setError((err as Error).message || m.loginFailed)
        } finally {
          polling = false
        }
      }, 5000)
    } catch (err) {
      setError((err as Error).message || m.loginFailed)
    } finally {
      setLoading(false)
    }
  }

  function cancelPhoneLogin() {
    stopPolling()
    setPendingLogin(null)
    setAccountChoices(null)
    setError(null)
  }

  async function chooseAccount(index: number) {
    if (!pendingLogin) return
    setLoading(true)
    setError(null)
    try {
      await finishLogin(await api.loginPhoneConfirm(pendingLogin.loginId, index))
    } catch (err) {
      cancelPhoneLogin()
      setError((err as Error).message || m.loginFailed)
    } finally {
      setLoading(false)
    }
  }

  function handleLogout() {
    setError(null)
    void signOut(queryClient)
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <h1 className="text-2xl font-bold text-slate-900">{m.title}</h1>
      <p className="mt-2 text-sm text-slate-500">
        {tab === 'phone'
          ? m.introPhone
          : m.introPassword}
      </p>

      {hasAuth() ? (
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-lg p-5">
          <p className="text-sm text-emerald-900">{m.loggedIn}</p>
          <button
            onClick={handleLogout}
            className="mt-3 text-sm text-emerald-700 underline hover:text-emerald-900"
          >
            {m.logout}
          </button>
        </div>
      ) : (
        <>
          {methods?.phone && (
            <div className="mt-6 flex rounded-lg border border-slate-200 p-1 text-sm">
              <button
                type="button"
                onClick={() => { setTab('password'); cancelPhoneLogin() }}
                className={`flex-1 rounded-md px-3 py-1.5 ${tab === 'password' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {m.tabPassword}
              </button>
              <button
                type="button"
                onClick={() => { setTab('phone'); setError(null) }}
                className={`flex-1 rounded-md px-3 py-1.5 ${tab === 'phone' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {m.tabPhone}
              </button>
            </div>
          )}

          {tab === 'phone' && methods?.phone ? (
            accountChoices ? (
              <div className="mt-8 bg-slate-50 border border-slate-200 rounded-lg p-6">
                <p className="text-sm font-medium text-slate-900">{m.chooseAccount}</p>
                <p className="mt-1 text-sm text-slate-500">
                  {m.multipleAccounts}
                </p>
                <div className="mt-4 space-y-2">
                  {accountChoices.map((choice) => (
                    <button
                      key={choice.index}
                      type="button"
                      disabled={loading}
                      onClick={() => chooseAccount(choice.index)}
                      className="w-full text-left rounded border border-slate-300 bg-white px-4 py-3 text-sm hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-50 transition"
                    >
                      <span className="font-medium text-slate-900">
                        {choice.company || choice.name || m.accountN(choice.index + 1)}
                      </span>
                      {choice.company && choice.name && (
                        <span className="block text-slate-500">{choice.name}</span>
                      )}
                    </button>
                  ))}
                </div>
                {error && (
                  <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {error}
                  </div>
                )}
                <button
                  type="button"
                  onClick={cancelPhoneLogin}
                  className="mt-4 text-sm text-slate-500 underline hover:text-slate-700"
                >
                  {m.cancel}
                </button>
              </div>
            ) : pendingLogin ? (
              <div className="mt-8 bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
                <p className="text-sm text-slate-600">{m.securityNumberIs}</p>
                <p className="mt-2 text-4xl font-bold tracking-widest text-slate-900">
                  {pendingLogin.securityNumber}
                </p>
                <p className="mt-4 text-sm text-slate-500">
                  {m.confirmOnPhone}
                </p>
                <div className="mt-4 flex justify-center">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                </div>
                <button
                  type="button"
                  onClick={cancelPhoneLogin}
                  className="mt-4 text-sm text-slate-500 underline hover:text-slate-700"
                >
                  {m.cancel}
                </button>
              </div>
            ) : (
              <form onSubmit={handlePhoneSubmit} className="mt-8 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">{m.phone}</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                    required
                    className={inputClass}
                  />
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium rounded px-4 py-2 transition"
                >
                  {loading ? m.sending : m.authenticate}
                </button>
              </form>
            )
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  {m.emailOrUsername}
                </label>
                <input
                  type="text"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  autoComplete="username"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">{m.password}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className={inputClass}
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium rounded px-4 py-2 transition"
              >
                {loading ? m.loggingIn : m.login}
              </button>
            </form>
          )}

          {methods?.accessRequests && (
          <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-5">
            <p className="text-sm font-medium text-slate-900">{m.noAccess}</p>
            <p className="mt-1 text-sm text-slate-500">
              {m.noAccessBody}
            </p>
            <Link
              to="/umsokn"
              className="mt-3 inline-block rounded border border-emerald-600 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition"
            >
              {m.applyForAccess}
            </Link>
          </div>
          )}
        </>
      )}
    </div>
  )
}
